import TextInput from '@/shared/Inputs/TextInput'
import { useRef, useState } from 'react'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import MapIcon from '@/assets/icons/map-icon.svg?react'
import useDebounce from '@/shared/hooks/useDebounce.ts'
import { IExternalDetailsResponse, IGeoSuggestExternalResponseItem } from '@/services/geoSuggest/geoSuggest.types.ts'
import geoSuggest from '@/services/geoSuggest'
import DropdownFadeInPanel from '@/shared/Dropdowns/DropdownFadeInPanel'
import usePopup from '@/shared/hooks/usePopup.ts'
import DropdownListItem from '@/shared/Dropdowns/DropdownListItem'
import useSkipFirstRenderEffect from '@/shared/hooks/useSkipFirstRenderEffect.ts'
import CircleLoader from '@/shared/Loaders/CircleLoader'

interface IProps {
  error?: boolean
  inscription?: string
  addressLine: string
  onChange: (value: TAddressInputData) => void
  onSearchChange?: (value: string) => void
}

export type TAddressInputData = IExternalDetailsResponse

const AddressInput = (props: IProps) => {
  const ref = useRef<HTMLDivElement>()
  const valueSelected = useRef(false)

  const { open, setOpen } = usePopup(ref)

  const { wait: waitDetails, loading: detailsLoading } = useHttpLoader()
  const { wait, loading: suggestionsLoading } = useHttpLoader()
  const [search, setSearch] = useState(props.addressLine)
  const [suggestions, setSuggestions] = useState<IGeoSuggestExternalResponseItem[]>([])
  const debouncedSearch = useDebounce(search, 500)

  useSkipFirstRenderEffect(() => {
    if (!debouncedSearch) return
    if (valueSelected.current) {
      valueSelected.current = false
      return
    }

    wait(geoSuggest.getSuggestions(search), (resp) => {
      setSuggestions(resp.results)
      setOpen(true)
    })
  }, [debouncedSearch])

  const handleSelect = (option: IGeoSuggestExternalResponseItem) => {
    setOpen(false)

    waitDetails(geoSuggest.getSuggestDetails(option.uri), (resp) => {
      if (resp) {
        setSearch(resp.address)
        valueSelected.current = true
        props.onChange(resp)
      }
    })
  }

  const renderIcon = () => {
    if (suggestionsLoading || detailsLoading) {
      return <CircleLoader color={props.error ? 'var(--error)' : 'var(--border-color)'} style={{ marginTop: '-2px' }} />
    }
    return <MapIcon className="text-placeholder" />
  }

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <TextInput
        value={search}
        name="search"
        onChange={(v) => setSearch(v)}
        placeHolder="Начните вводить адрес.."
        disabled={detailsLoading}
        icon={renderIcon()}
        error={props.error}
        inscription={props.inscription}
      />
      {suggestions.length > 0 && (
        <DropdownFadeInPanel open={open} onClick={() => setOpen(false)}>
          {suggestions.map((s) => (
            <DropdownListItem key={s.uri} onClick={() => handleSelect(s)}>
              <p className="text-nm" style={{ color: 'inherit' }}>
                {s.cityAndRegion + ' ' + s.address}
              </p>
            </DropdownListItem>
          ))}
        </DropdownFadeInPanel>
      )}
    </div>
  )
}

export default AddressInput
